/**
 * Inhaltsformat INNERHALB der Verschlüsselung.
 *
 * Der Typ einer Nachricht — Text oder Anrufsignal — steckt bewusst im
 * verschlüsselten Teil, nicht im Envelope. Der Server kann dadurch nicht
 * unterscheiden, ob gerade geschrieben oder telefoniert wird. Stünde der Typ
 * außen, wäre "wer telefoniert wann mit wem" eine Metadatenspur, die er
 * mitschreiben könnte.
 */

export type CallSignalKind = 'offer' | 'answer' | 'ice' | 'hangup' | 'reject' | 'busy';

export type Payload =
  /**
   * `messageId` ist die LOGISCHE Kennung der Nachricht, gleich fuer alle
   * Kopien. Die Envelope-Kennung taugt dafuer nicht: Sie ist je Zielgeraet
   * verschieden, und eine Lesebestaetigung, die sie nennt, traefe beim
   * Absender keinen Eintrag. Optional, damit aeltere Nachrichten lesbar
   * bleiben — ohne sie gibt es eben keine Bestaetigung.
   */
  | { kind: 'text'; text: string; messageId?: string }
  /**
   * Lesebestaetigung: Die genannten Nachrichten wurden angezeigt.
   *
   * Sie geht an ALLE Geraete des Absenders, nicht nur an das sendende — sonst
   * zeigte sein Telefon gelesen und sein Notebook nicht.
   */
  | { kind: 'read'; messageIds: string[] }
  /**
   * Kopie einer eigenen ausgehenden Nachricht an die eigenen anderen Geraete.
   *
   * Signal-Semantik (docs/01-architektur.md §6): Eigene Geraete bekommen
   * dieselbe Nachricht ueber denselben Weg. Ohne das sieht man auf dem
   * zweiten Geraet nur die Antworten der Gegenseite und nicht, was man selbst
   * geschrieben hat — ein Verlauf mit Loechern.
   *
   * `peerAccountId` sagt, zu welchem Gespraech die Kopie gehoert: Das
   * empfangende Geraet kann das nicht aus dem Absender ableiten, denn Absender
   * ist man selbst.
   */
  | {
      kind: 'sync';
      peerAccountId: string;
      messageId: string;
      text: string;
      timestamp: number;
    }
  | {
      kind: 'call';
      callId: string;
      signal: CallSignalKind;
      video: boolean;
      sdp?: string;
      candidate?: RTCIceCandidateInit;
    };

export function encodePayload(payload: Payload): string {
  return JSON.stringify(payload);
}

/**
 * Ältere Nachrichten sind reiner Text ohne Hülle. Lässt sich etwas nicht als
 * Payload lesen, wird es als Text behandelt — ein Formatwechsel darf den
 * bestehenden Verlauf nicht unlesbar machen.
 */
export function decodePayload(raw: string): Payload {
  try {
    const parsed = JSON.parse(raw) as Partial<Payload>;
    if (
      parsed &&
      (parsed.kind === 'text' ||
        parsed.kind === 'call' ||
        parsed.kind === 'read' ||
        parsed.kind === 'sync')
    ) {
      return parsed as Payload;
    }
  } catch {
    // Kein JSON — also Klartext aus der Zeit vor diesem Format.
  }
  return { kind: 'text', text: raw };
}
